import React, { createElement } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DocumentList from '../../../components/lists/DocumentList';
import FactoringHeader from '../../../components/headers/FactoringHeader';
import BlockOperationDialog from '../../../components/dialogs/BlockOperationDialog';
import CLIENT_OFFERS from '../../../graphql/queries/factoring/client-offers';
import AvailableOffersFilters from '../components/filters/AvailableOffersFilters';
import {
  OUTSIDE_BOX_PROPS_FOR_HORIZONTAL_SCROLL,
  DETAILED_DATA_GRID_CLASS_NAME,
} from '../../../theme/constants/data-grid';

const AvailableOffersView = ({
  getColumns,
  includeHeaders,
  mobileHeaders,
  OverlayComponent,
  disabledRowTooltip,
  customHeaderProps,
  stepConfiguration,
  gridMinHeight,
  isMobile,
  currency,
  offerIds,
  showConfirmationSummary,
  setShowConfirmationSummary,
  getRowClassName,
  isRowSelectable,
  handleSelectionModelChange,
  onCompletedSetRows,
  enableBlockOperationFlow,
  isBlocked,
  unavailabilityCode,
  blockOperationDialogProps,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <DocumentList
        trackerId="OFFERS"
        type="available-offers"
        headerTitle={t('available-offers-header-title')}
        queryDocument={CLIENT_OFFERS}
        initialOrderBy="-folio"
        CustomHeader={FactoringHeader}
        gridMinHeight={gridMinHeight}
        customHeaderProps={{
          Filters: AvailableOffersFilters,
          ...customHeaderProps,
        }}
        showConfirmationSummary={showConfirmationSummary}
        setShowConfirmationSummary={setShowConfirmationSummary}
        ConfirmationSummary={
          createElement(stepConfiguration.component, stepConfiguration.props)
        }
        checkboxSelection
        emitter
        countryFilter
        includeHeaders={includeHeaders}
        mobileHeaders={mobileHeaders}
        customVariables={{
          currency,
          hasValidOffer: true,
          dateIssued_Gte: null,
          dateIssued_Lte: null,
          useCacheCount: false,
        }}
        disabledRowTooltip={disabledRowTooltip}
        onCompletedSetRows={onCompletedSetRows}
        onSelectionModelChange={handleSelectionModelChange}
        disableSelectionOnClick={false}
        selectionModel={offerIds}
        keepNonExistentRowsSelected
        preColumns={getColumns(isMobile)}
        isRowSelectable={isRowSelectable}
        getRowClassName={getRowClassName}
        isCellEditable={({ row }) => !row.isForOrdering}
        DataGridNoRowsOverlayComponent={OverlayComponent}
        dataGridClassName={DETAILED_DATA_GRID_CLASS_NAME}
        mobileDensity="standard"
        rowHeight={65}
        outsideBoxProps={isMobile ? OUTSIDE_BOX_PROPS_FOR_HORIZONTAL_SCROLL : {}}
      />
      {enableBlockOperationFlow && isBlocked && (
        <BlockOperationDialog
          {...blockOperationDialogProps}
          unavailabilityCode={unavailabilityCode}
        />
      )}
    </>
  );
};

AvailableOffersView.propTypes = {
  getColumns: PropTypes.func.isRequired,
  includeHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
  mobileHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
  OverlayComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  disabledRowTooltip: PropTypes.func,
  customHeaderProps: PropTypes.shape({}),
  stepConfiguration: PropTypes.shape({
    component: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
    props: PropTypes.shape({}).isRequired,
  }).isRequired,
  gridMinHeight: PropTypes.number,
  isMobile: PropTypes.bool.isRequired,
  currency: PropTypes.string,
  offerIds: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string, PropTypes.number,
  ])).isRequired,
  showConfirmationSummary: PropTypes.bool.isRequired,
  setShowConfirmationSummary: PropTypes.func.isRequired,
  getRowClassName: PropTypes.func.isRequired,
  isRowSelectable: PropTypes.func.isRequired,
  handleSelectionModelChange: PropTypes.func.isRequired,
  onCompletedSetRows: PropTypes.func.isRequired,
  enableBlockOperationFlow: PropTypes.bool,
  isBlocked: PropTypes.bool,
  unavailabilityCode: PropTypes.string,
  blockOperationDialogProps: PropTypes.shape({}),
};

AvailableOffersView.defaultProps = {
  disabledRowTooltip: undefined,
  customHeaderProps: {},
  gridMinHeight: undefined,
  currency: undefined,
  enableBlockOperationFlow: false,
  isBlocked: false,
  unavailabilityCode: undefined,
  blockOperationDialogProps: undefined,
};

export default AvailableOffersView;
